import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'

const NOEMAIL_DOMAIN = 'noemail.local'
const normalizeLogin = (val) => val.trim().includes('@') ? val.trim() : `${val.trim()}@${NOEMAIL_DOMAIN}`

export default function LoginPage() {
  const { signIn, forgotPassword } = useAuth()
  const [login, setLogin]         = useState('')
  const [password, setPassword]   = useState('')
  const [error, setError]         = useState('')
  const [loading, setLoading]     = useState(false)
  const [forgotMode, setForgotMode] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')

  const handleSignIn = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    const { error } = await signIn(normalizeLogin(login), password)
    if (error) setError(error.message)
    setLoading(false)
  }

  const handleForgot = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { error } = await forgotPassword(forgotEmail.trim())
      if (error) throw error
      setForgotSent(true)
    } catch (err) {
      setError(err.message || 'Failed to send reset email')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-black flex flex-col items-center justify-center px-4">
      {/* Top accent bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-brand-red" />

      <div className="w-full max-w-sm">
        {/* Logo block */}
        <div className="mb-8 text-center">
          <img src="/logo-alt.png" alt="Clemens Aviation" className="h-12 w-auto mx-auto mb-5" />
          <h1 className="text-lg font-semibold text-white">
            {forgotMode ? 'Reset your password' : 'Master Schedule'}
          </h1>
          <p className="text-sm text-white/40 mt-1">
            {forgotMode ? "Enter your email and we'll send a reset link" : 'Sign in to access the schedule'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl shadow-black/30 p-7">
          {forgotSent ? (
            <div className="text-center py-2">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="text-sm font-semibold text-gray-900 mb-1">Check your email</div>
              <p className="text-sm text-gray-500">
                We sent a reset link to <strong>{forgotEmail}</strong>.
              </p>
              <button
                onClick={() => { setForgotMode(false); setForgotSent(false) }}
                className="mt-4 text-sm text-brand-red hover:text-brand-red-dark font-medium"
              >
                Back to sign in
              </button>
            </div>
          ) : forgotMode ? (
            <form onSubmit={handleForgot} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  required
                  autoFocus
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-colors"
                  placeholder="you@example.com"
                />
                <p className="text-xs text-gray-400 mt-1.5">Technician accounts don't use email — ask your administrator to reset your password.</p>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-red hover:bg-brand-red-dark text-white text-sm font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50 transition-colors"
              >
                {loading ? 'Sending…' : 'Send Reset Link'}
              </button>
              <button
                type="button"
                onClick={() => { setForgotMode(false); setError('') }}
                className="w-full text-sm text-gray-400 hover:text-gray-600 py-1 transition-colors"
              >
                Back to sign in
              </button>
            </form>
          ) : (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Username</label>
                <input
                  type="text"
                  name="username"
                  required
                  autoFocus
                  autoComplete="username"
                  value={login}
                  onChange={e => setLogin(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-colors"
                  placeholder="you@example.com or jsmith"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Password</label>
                <input
                  type="password"
                  name="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-colors"
                  placeholder="••••••••"
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-red hover:bg-brand-red-dark disabled:opacity-50 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors mt-2"
              >
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
              <button
                type="button"
                onClick={() => { setForgotMode(true); setError('') }}
                className="w-full text-sm text-gray-400 hover:text-gray-600 py-1 transition-colors"
              >
                Forgot your password?
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-white/20 text-xs mt-6">
          © {new Date().getFullYear()} Clemens Aviation
        </p>
      </div>
    </div>
  )
}
